/**
 * DEAD SIGNAL — Hint System
 *
 * Deterministic hint chains from DEAD_SIGNAL_Hint_Chains.md.
 * Four-level escalation per objective, phase-keyed to Craine's
 * current personality phase. Zero LLM cost.
 *
 * Hint chain DATA lives in the active chapter's content chunk
 * (e.g. content/chapters/chapter1/hints.js) and is accessed via
 * getHintChains() from the active-chapter registry (ADR-0001).
 */
import { useGameStore } from '../state/gameState.js';
import { getHintChains } from '../content/activeChapter.js';
import { selectCh6Objective } from './hintSelection.js';

// ── Cooldown responses ───────────────────────────────
const COOLDOWN_RESPONSES = [
  '"I just told you what I think. Give me a minute."',
  '"Still chewing on the same thing. Hasn\'t changed."',
  '"You heard me. I\'m waiting on you now."',
  '"Same answer as thirty seconds ago. Go do something with it."',
];

/**
 * Get the appropriate hint for the current objective and hint level.
 * Returns dialogue lines to add to the panel, or null if no hint available.
 */
export function getHint(objectiveId) {
  const store = useGameStore.getState();
  const chain = getHintChains()[objectiveId];

  if (!chain) return null;

  // Check cooldown
  if (store.hintCooldown > 0) {
    const response = COOLDOWN_RESPONSES[Math.floor(Math.random() * COOLDOWN_RESPONSES.length)];
    return [{ text: response, type: 'npc', speaker: 'CRAINE' }];
  }

  // Get current level and escalate
  const currentLevel = store.getHintLevel(objectiveId);
  const hintIndex = Math.min(currentLevel, chain.hints.length - 1);
  const hint = chain.hints[hintIndex];

  store.escalateHint(objectiveId);

  const lines = [];
  lines.push({ text: hint.text, type: 'npc', speaker: 'CRAINE' });

  return lines;
}

/**
 * Determine the most relevant objective for the current game state.
 */
export function getCurrentObjective() {
  const { chapter, sceneId, evidence, interview, exhaustion } = useGameStore.getState();

  // Chapter 6 — Hargrove Interview demo slice (never during the interview).
  if (chapter === 6 || sceneId === 'ds_helios_office') {
    return selectCh6Objective({
      interviewActive: !!interview?.active,
      suppressedCount: interview?.suppressedMessages?.length || 0,
      hasRoutedAround: !!interview?.hasRoutedAround,
      reEngagementCount: exhaustion?.reEngagementCount || 0,
      pressedWithoutEvidence: exhaustion?.pressedWithoutEvidence || [],
      exploredTopics: exhaustion?.exploredTopics || [],
      evidence,
    });
  }

  if (chapter === 1 && sceneId === 'recovery_ward') {
    return '1.2_demonstrate_capabilities';
  }

  if (chapter === 2 && sceneId === 'delacroix_apt') {
    // Living-Rena visit (alive-canon): she walks Craine through her
    // investigation wall (BEACON_ARTICLE) and her case notebook. The
    // post-death forensic objectives (wall gap, water glass) moved to the
    // ch8 crime-scene revisit with their evidence records.
    if (!evidence.includes('BEACON_ARTICLE')) {
      return '2.1_examine_wall';
    }
    if (!evidence.includes('DELACROIX_NOTEBOOK')) {
      return '2.2_find_notebook';
    }
  }

  return null;
}
